"use client";

import type {
  ExpandableContentProps,
  ExpandableProps,
  ExpandableRootProps,
  ExpandableTriggerProps,
} from "./types";

import React, { useContext } from "react";

import twMerge from "@replo/design-system/utils/twMerge";
import { LucideChevronDown } from "lucide-react";
import { useControllableState } from "replo-utils/react/use-controllable-state";

import Button from "../button/Button";
import { ExpandableContext } from "./ExpandableContext";

const ExpandableRoot: React.FC<
  React.PropsWithChildren<ExpandableRootProps>
> = ({ children, defaultOpen = false, open, onOpenChange }) => {
  const [isOpen, setIsOpen] = useControllableState(
    open,
    defaultOpen,
    onOpenChange,
  );
  return (
    <ExpandableContext.Provider value={{ isOpen, setOpen: setIsOpen }}>
      {children}
    </ExpandableContext.Provider>
  );
};

const ExpandableTrigger: React.FC<ExpandableTriggerProps> = ({ children }) => {
  const context = useContext(ExpandableContext);
  if (!context) {
    // eslint-disable-next-line replo/no-raw-error-throw
    throw new Error(
      "Expandable.Trigger must be used within an Expandable.Root",
    );
  }
  const { isOpen, setOpen } = context;

  return (
    <Button
      variant="tertiary"
      onClick={() => setOpen(!isOpen)}
      endEnhancer={
        <LucideChevronDown
          className={twMerge(
            "transition-transform",
            isOpen ? "rotate-180" : "",
          )}
          size={12}
        />
      }
    >
      {typeof children === "function" ? children(context) : children}
    </Button>
  );
};

const ExpandableContent: React.FC<ExpandableContentProps> = ({ children }) => {
  const context = useContext(ExpandableContext);
  if (!context) {
    // eslint-disable-next-line replo/no-raw-error-throw
    throw new Error(
      "Expandable.Content must be used within an Expandable.Root",
    );
  }
  const { isOpen } = context;

  return (
    <div
      className={twMerge(
        "transform overflow-hidden transition-all duration-300 ease-in-out",
        isOpen
          ? `max-h-[1000px] translate-y-0 opacity-100`
          : "max-h-0 -translate-y-2 opacity-0",
      )}
    >
      {children}
    </div>
  );
};

export const Expandable = (props: ExpandableProps) => {
  return (
    <ExpandableRoot {...props}>
      <ExpandableTrigger>{props.trigger}</ExpandableTrigger>
      <ExpandableContent>{props.content}</ExpandableContent>
    </ExpandableRoot>
  );
};

Expandable.Root = ExpandableRoot;
Expandable.Trigger = ExpandableTrigger;
Expandable.Content = ExpandableContent;
